import { useSessionValue } from '@portal/appLayer/auth';
import { Metafield } from '@portal/gql/shopifyStorefront/graphql';
import { useGateValue } from '@statsig/react-bindings';
import {
  isGrandFatherAGZ,
  normalizeVariantId
} from '@portal/shared/components/V53EditProductsModal/utils';
import { CollectionProduct } from '@portal/api/shopifyV2/types';
import { FLAVOR_CONFIG } from '@portal/constants/flavors';
import { AlternateFlavor } from '@portal/processes/ShopV2/ShopV2.types';
import { useCallback, useMemo } from 'react';
import {
  SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID,
  SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID,
  SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID
} from '@portal/config/base';
import { isPouchOrTravelPackV53Variant } from '@portal/shared/utils/shopify/isPouchOrTravelPackV53Variant';
import { getShopifyVariantId } from '@portal/shared/utils/subscriptionV2Helpers';
import { SSGates } from '../context/StatsigContext/StatsigFlags';
import { useStoreValue } from './useStore';
import { ALT_FLAVORS_BY_STORE } from './useSplitAlternateFlavorVariants/constants';

interface AgzAccount {
  isAgzAccount: boolean;
  hasReceivedAgz: boolean;
  isGrandfatheredAgz: boolean;
}
interface UseAgzAccountProps extends AgzAccount {
  isAgzGateEnabled: boolean;
  filterAGZPricingProducts: (
    products: CollectionProduct[],
    existingSubs: string[]
  ) => CollectionProduct[];
  filterAGZFlavorsWithGate: (products: AlternateFlavor[]) => AlternateFlavor[];
  recheckSession: () => AgzAccount;
}

const AGZ_PHASE_1_TAG = 'AGZ Phase 1';

/**
 * Hook to determine AGZ status
 * @returns {UseAgzAccountProps}
 * @property {boolean} isAgzAccount       - Auth'd users only - indicates if the user is eligible for AGZ
 * @property {boolean} hasReceivedAgz     - Auth'd users only - indicates if the user has received the AGZ welcome kit
 * @property {boolean} isGrandfatheredAgz - Auth'd users only - indicates grandfathered status based on AGZ Phase 1 tag
 * @property {boolean} isAgzGateEnabled   - Indicates AGZ gate status regardless of auth status
 * @property {function} recheckSession    - Function to re-evaluate AGZ account status
 */
export function useAGZAccount(): UseAgzAccountProps {
  const session = useSessionValue();
  const isAgzGateEnabled = useGateValue(SSGates.enableAGZ);
  const store = useStoreValue();

  const recheckSession = useCallback((): AgzAccount => {
    let isAgzAccount = false;
    let hasReceivedAgz = false;
    let isGrandfatheredAgz = false;

    if (session?.customer) {
      const { customer } = session;

      // AGZ Phase 1 tag determines grandfathered pricing
      const customerHasAGZTag = customer.tags?.includes(AGZ_PHASE_1_TAG) ?? false;
      isGrandfatheredAgz = customerHasAGZTag;

      // TODO - remove customerHasAGZTag condition after full launch
      // Keep for now to handle existing AGZ P1 customers who may fall outside the US/CA launch gate
      isAgzAccount = customerHasAGZTag || isAgzGateEnabled;

      const metafield = customer.metafields?.find(
        (mf: Metafield) => mf.key === 'agz_welcome_box_received'
      );

      hasReceivedAgz = metafield?.value === true || metafield?.value === 'true';
    }
    return { isAgzAccount, hasReceivedAgz, isGrandfatheredAgz };
  }, [session, isAgzGateEnabled]);

  const { isAgzAccount, hasReceivedAgz, isGrandfatheredAgz } = useMemo(
    () => recheckSession(),
    [recheckSession]
  );

  const filterAGZPricingProducts = useCallback(
    (products: CollectionProduct[], existingSubs: string[]) => {
      const discountedAgzVariantIds = Object.values(
        SHOPIFY_AGZ_TRAVEL_PACK_DISCOUNTED_SUB_VARIANT_ID[store]
      );
      const regularAgzVariantIds = Object.values(
        SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]
      );
      const otpAgzVariantIds = Object.values(SHOPIFY_AGZ_TRAVEL_PACK_OTP_VARIANT_ID[store]);

      const isAg1PresentInSubs = existingSubs.some((id) => {
        return isPouchOrTravelPackV53Variant(getShopifyVariantId(id), store);
      });

      const agzOtpFilteredOut = products.filter(({ variants }) => {
        const { id } = variants[0];
        return !otpAgzVariantIds.includes(normalizeVariantId(id));
      });
      const mixedBerryProductIds = ALT_FLAVORS_BY_STORE[store]
        .filter(({ title }) => title === FLAVOR_CONFIG.mixedBerry.title)
        .map(({ id }) => id);
      const filteredProducts = isAgzGateEnabled
        ? agzOtpFilteredOut
        : agzOtpFilteredOut.filter(({ id }) => {
            return !mixedBerryProductIds.includes(id);
          });

      // Temporary Fix for if the logic did not work and there were discounted AGZ in the cart while user is not
      // tagged with grandfather and doesn't have a pouch in sub could still view the agz sub
      const isGrandfatheredAgzInLineItems = existingSubs.some((id) => {
        return isGrandFatherAGZ(normalizeVariantId(id), store);
      });

      if (isGrandfatheredAgz || isAg1PresentInSubs || isGrandfatheredAgzInLineItems) {
        return filteredProducts.filter((product) => {
          const variantIds = product.variants.map((variant) => normalizeVariantId(variant.id));

          return !variantIds.some((variantId) => regularAgzVariantIds.includes(variantId));
        });
      }
      return filteredProducts.filter((product) => {
        const variantIds = product.variants.map((variant) => normalizeVariantId(variant.id));
        return !variantIds.some((variantId) => discountedAgzVariantIds.includes(variantId));
      });
    },
    [isGrandfatheredAgz, store, isAgzGateEnabled]
  );

  const filterAGZFlavorsWithGate = useCallback(
    (flavors: AlternateFlavor[]) => {
      if (isAgzGateEnabled) return flavors;

      // Filter out Mixed Berry if the flag is not on
      const mixedBerryProductIds = ALT_FLAVORS_BY_STORE[store]
        .filter(({ title }) => title === FLAVOR_CONFIG.mixedBerry.title)
        .map(({ id }) => id);

      return flavors.filter(({ id }) => {
        return !mixedBerryProductIds.includes(id);
      });
    },
    [isAgzGateEnabled, store]
  );

  return {
    isAgzAccount,
    hasReceivedAgz,
    isGrandfatheredAgz,
    isAgzGateEnabled,
    recheckSession,
    filterAGZPricingProducts,
    filterAGZFlavorsWithGate
  };
}
